import { Component } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { helper } from 'src/environments/helper';
import { Observable, finalize, map } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { IAddCererePayload, IFormCetateanValue, IFormValue } from './add-cerere.interface';
import { CereriService } from '../cereri.service';
import { CommonService } from 'src/app/services/common.service';
import { phoneValidator } from 'src/app/validators/phone-validator';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { initializeEditor } from 'src/app/utils/editorConfig';
import { environment } from 'src/environments/environment';
import { judete } from './judete';

@Component({
    selector: 'app-add-cerere',
    templateUrl: './add-cerere.component.html',
    styleUrls: ['./add-cerere.component.scss']
})
export class AddCerereComponent {
    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;

    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;

    helper = helper;
    isLoading: boolean = false;
    showCetateanFields: boolean = false;
    editorConfig!: AngularEditorConfig;
    uploading: boolean = false;

    defaultParameter: { name: string; id: number } = {
        name: '--Alegeți--',
        id: -1,
    };
    sectoare: { name: string; id: number }[] = [
        { name: '--Alegeți--', id: -1 },
        { name: '1', id: 1 },
        { name: '2', id: 2 },
        { name: '3', id: 3 },
        { name: '4', id: 4 },
        { name: '5', id: 5 },
        { name: '6', id: 6 },
    ];

    prioritatiCerere$!: Observable<Generic[]>;
    compartimenteDeps$!: Observable<Generic[]>;
    tipuriCerere$!: Observable<Generic[]>;
    operatori$!: Observable<Generic[]>;
    judete$!: Observable<Generic[]>;
    // judete: Generic[] = [this.defaultParameter, ...judete];

    form: FormGroup = new FormGroup({
        subject: new FormControl('', [Validators.required]),
        id_cerere_priority: new FormControl('', [Validators.required, Validators.min(0)]),
        id_cerere_status: new FormControl(-1, []),
        id_departament: new FormControl('', [Validators.required, Validators.min(0)]),
        id_cerere_type: new FormControl('', [Validators.required, Validators.min(0)]),
        id_user_assigned: new FormControl('', []),
        description: new FormControl('', [Validators.required]),
        files: this.fb.array([]),
        check_cetatean: new FormControl(false, []),
    });

    formCetatean: FormGroup = new FormGroup({
        cet_nume: new FormControl('', [Validators.required]),
        cet_prenume: new FormControl('', [Validators.required]),
        cet_telefon: new FormControl('', [phoneValidator()]),
        cet_email: new FormControl('', [Validators.email]),
        cet_cnp: new FormControl('', [Validators.required]),
        cet_ci_serie: new FormControl('', [Validators.required]),
        cet_ci_numar: new FormControl('', [Validators.required]),
        cet_adr_judet: new FormControl(-1, [Validators.required, Validators.min(0)]),
        cet_adr_oras: new FormControl('', [Validators.required]),
        cet_adr_sector: new FormControl(-1, []),
        cet_adr_strada: new FormControl('', [Validators.required]),
        cet_adr_numar: new FormControl('', [Validators.required]),
        cet_adr_bloc: new FormControl('', []),
        cet_adr_scara: new FormControl('', []),
        cet_adr_etaj: new FormControl('', []),
        cet_adr_apartament: new FormControl('', []),
        cet_adr_observatii: new FormControl('', [])
    })

    constructor(
        private dialogRef: MatDialogRef<AddCerereComponent>,
        private notificationService: NotificationService,
        private cereriService: CereriService,
        private commonService: CommonService,
        private fb: FormBuilder,
    ) { }

    ngOnInit(): void {
        this.loadCererePriorities();
        this.loadCompartimenteDeps();
        this.loadJudete();
        this.editorConfig = initializeEditor();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    loadCererePriorities() {
        this.isLoading = true;
        this.prioritatiCerere$ = this.commonService.getCererePrioritiesActive().pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => (this.isLoading = false))
        );
    }

    loadCompartimenteDeps() {
        this.isLoading = true;
        this.compartimenteDeps$ = this.commonService.getCompartimenteByDeps().pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => (this.isLoading = false))
        );
    }

    onCompartimenteChange(compartimentId: number): void {
        this.form.controls['id_cerere_type'].patchValue(-1);
        this.form.controls['id_user_assigned'].patchValue(-1);

        if (compartimentId > 0) {
            this.loadCerereTypes(compartimentId);
            this.loadOperatori(compartimentId);
        } else {
            this.tipuriCerere$ = new Observable<Generic[]>((observer) => {
                observer.next([this.defaultParameter])
                observer.complete();
            });
            this.operatori$ = new Observable<Generic[]>((observer) => {
                observer.next([this.defaultParameter])
                observer.complete();
            });
        }
    }

    loadCerereTypes(compartimentId: number) {
        this.isLoading = true;
        this.tipuriCerere$ = this.commonService.getCerereTypesByComp(compartimentId).pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => (this.isLoading = false))
        );
    }

    loadOperatori(id_compartiment: number) {
        this.isLoading = true;
        this.operatori$ = this.cereriService.getOperatoriByCompartiment(id_compartiment).pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => { this.isLoading = false })
        );
    }

    loadJudete() {
        this.isLoading = true;
        this.judete$ = this.commonService.getJudeteActive().pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => { this.isLoading = false })
        );
    }

    toggleCetatean() {
        const checkCetateanValue = this.form.value.check_cetatean;
        this.showCetateanFields = checkCetateanValue;
    }

    submitForm() {
        this.isLoading = true;

        const formValue: IFormValue = this.form.value;
        const formCetateanValue: IFormCetateanValue = this.formCetatean.value;

        const payload: IAddCererePayload = { ...formValue, ...formCetateanValue };

        this.cereriService
            .addCerere(payload)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    addFile(event: any) {
        event.eventPreventDefault = true;
        const inputElement = event.target as HTMLInputElement;
        if (!inputElement.files) {
            return;
        }

        const files = inputElement.files;

        // Iterate through the selected files and append them to the 'files' array in the form
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            this.uploading = true;

            const formData = new FormData();
            formData.append('file', file, file.name);

            this.cereriService.fileUpload(formData).subscribe({
                next: (result: any) => {
                    let response =
                        typeof result.status_code !== 'undefined' ? result : result.error;
                    if (typeof response.status_code !== 'undefined') {
                        if (
                            response.status_code == 200 &&
                            typeof response.data !== 'undefined'
                        ) {
                            // Handle the uploaded file by adding it to the 'files' array in the form
                            const files = this.form.get(
                                'files'
                            ) as FormArray;
                            files.push(new FormControl(result.data));

                            this.uploading = false;
                            this.notificationService.warningSwal(
                                this.successTitle,
                                helper.dialogConfig.generalMessages.fileSuccessfullyUploaded,
                                this.successIcon,
                            );
                        }
                    } else {
                        let errorMessage =
                            environment.config.customNotifications.generalMessages.error;
                        this.notificationService.warningSwal(
                            this.errorTitle,
                            errorMessage,
                            this.errorIcon
                        );
                        this.isLoading = false;
                        this.uploading = false;
                    }
                },
                error: (result: any) => {
                    this.isLoading = false;
                    this.uploading = false;

                    let msg;
                    try {
                        // try to catch the message from server
                        msg = result.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
        }
    }

    removeFile(index: number) {
        const files = this.form.get('files') as FormArray;
        if (index > -1 && index < files.length) {
            files.removeAt(index);
        }
    }
}
